import React, { createContext, useState, useContext, useMemo, ReactNode, useEffect } from 'react';
import PdfDocumentWrapper from './PdfDocumentWrapper';

interface PageMapping {
  file: PdfDocumentWrapper;
  originalPageNumber: number;
}

interface SelectedFilesContextType {
  files: PdfDocumentWrapper[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<PdfDocumentWrapper[]>>;
  pageToFileMap: Map<number, PageMapping>;
  fileToPagesMap: Map<string, Map<number, number>>;
}

const SelectedFilesContext = createContext<SelectedFilesContextType | undefined>(undefined);

const calculatePageNumberToFileMap = (files: PdfDocumentWrapper[]):
    { pageNumberToFileMap: Map<number, PageMapping>; fileToPagesMap: Map<string, Map<number, number>> } => {
  const pageNumberToFileMap = new Map<number, PageMapping>();
  const fileToPagesMap = new Map<string, Map<number, number>>();
  let currentPage = 1;

  files.forEach((file) => {
    const { document } = file;
    let filePages = new Map<number, number>();
    for (let i = 0; i < document.numPages; i++) {
      pageNumberToFileMap.set(currentPage, { file: file, originalPageNumber: i + 1 });
      filePages.set( i + 1, currentPage )
      currentPage++;
    }
    fileToPagesMap.set(file.id, filePages)
  });
  return { pageNumberToFileMap, fileToPagesMap };
};

export const SelectedFilesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [files, setSelectedFiles] = useState<PdfDocumentWrapper[]>([]);
  const [pageToFileMap, setPageToFileMap] = useState<Map<number, PageMapping>>(new Map());
  const [fileToPagesMap, setFileToPagesMap] = useState<Map<string, Map<number, number>>>(new Map());

  useEffect(() => {
    const { pageNumberToFileMap, fileToPagesMap } = calculatePageNumberToFileMap(files);
    setPageToFileMap(pageNumberToFileMap);
    setFileToPagesMap(fileToPagesMap);
  }, [files]);

  const value = useMemo(
    () => ({
      files,
      setSelectedFiles,
      pageToFileMap,
      fileToPagesMap,
    }),
    [files, pageToFileMap, fileToPagesMap]
  );

  return <SelectedFilesContext.Provider value={value}>{children}</SelectedFilesContext.Provider>;
};

export default SelectedFilesProvider;

export const useSelectedFilesContext = (): SelectedFilesContextType => {
  const context = useContext(SelectedFilesContext);
  if (!context) {
    throw new Error('useSelectedFilesContext must be used within an SelectedFilesProvider');
  }
  return context;
};
